import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  Search, 
  ZoomIn, 
  Bookmark, 
  Star, 
  Share2, 
  MessageSquare, 
  Users,
  Settings,
  Eye,
  Sparkles,
  Zap,
  HelpCircle,
  BookOpen,
  Activity,
  CreditCard,
  MessageCircle,
  Camera,
  Send,
  Youtube,
  Globe,
  FileText,
  FileSpreadsheet,
  Layers,
  Triangle,
  FileQuestion,
  FileCode,
  Sun,
  Moon,
  Eye as EyeIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthContext } from '../hooks/useAuthContext';
import PDFRenderer from '../components/pdf/PDFRenderer';
import LaTeXEditorModal from '../components/pdf/LaTeXEditorModal';
import AIResultModal from '../components/pdf/AIResultModal';
import formatDate from '../utils/formatDate';
import formatCount from '../utils/formatCount';

import '../styles/pages/PDFViewer.scss';

const PDFViewer = () => {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuthContext();
  const scrollContainerRef = React.useRef(null);
  const isProgressRestored = React.useRef(false); // Prevent overwriting saved progress on mount
  
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('original'); // 'original' or 'latex'  ← must be BEFORE viewProgress

  // Separate progress tracking per view mode
  const [viewProgress, setViewProgress] = useState({
    original: { currentPage: 1, totalPages: 1, maxPageReached: 1 },
    latex:    { currentPage: 1, totalPages: 1, maxPageReached: 1 },
  });
  // Convenient accessors for the active view
  const currentPage    = viewProgress[viewMode].currentPage;
  const totalPages     = viewProgress[viewMode].totalPages;
  const maxPageReached = viewProgress[viewMode].maxPageReached;

  const setCurrentPage = (valOrFn) => setViewProgress(prev => {
    const cur = prev[viewMode].currentPage;
    const next = typeof valOrFn === 'function' ? valOrFn(cur) : valOrFn;
    return { ...prev, [viewMode]: { ...prev[viewMode], currentPage: next } };
  });
  const setTotalPages = (val) => setViewProgress(prev => ({
    ...prev,
    [viewMode]: { ...prev[viewMode], totalPages: typeof val === 'function' ? val(prev[viewMode].totalPages) : val }
  }));
  const setMaxPageReached = (valOrFn) => setViewProgress(prev => {
    const cur = prev[viewMode].maxPageReached;
    const next = typeof valOrFn === 'function' ? valOrFn(cur) : valOrFn;
    return { ...prev, [viewMode]: { ...prev[viewMode], maxPageReached: next } };
  });
  const [zoom, setZoom] = useState(100);
  const [bookmarkedPages, setBookmarkedPages] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [activeSection, setActiveSection] = useState(1);
  const [sidebarRightExpanded, setSidebarRightExpanded] = useState({
    rating: false,
    sources: false,
    aiHelp: true,
    askQuestion: false,
    quiz: false,
    friends: false,
    latex: true
  });

  const [isLatexModalOpen, setIsLatexModalOpen] = useState(false);
  const [generatedLatex, setGeneratedLatex] = useState('');

  const [aiModal, setAiModal] = useState({ open: false, loading: false, error: null, title: '', text: '', questions: null, materialTitle: '' });

  // Which pages the generated quiz should cover.
  const [quizScope, setQuizScope] = useState('all'); // 'all' | 'current' | 'range'
  const [quizFrom, setQuizFrom] = useState(1);
  const [quizTo, setQuizTo] = useState(1);

  const runAiTextAction = async (endpoint, title) => {
    setAiModal({ open: true, loading: true, error: null, title, text: '', questions: null, materialTitle: '' });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/ai/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ pdfId: id, currentPage, totalPages })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'AI request failed.');
      setAiModal({ open: true, loading: false, error: null, title, text: data.result, questions: null, materialTitle: '' });
    } catch (err) {
      setAiModal({ open: true, loading: false, error: err.message, title, text: '', questions: null, materialTitle: '' });
    }
  };

  const runAiQuiz = async (format, label) => {
    setAiModal({ open: true, loading: true, error: null, title: label, text: '', questions: null, materialTitle: '' });
    try {
      const body = { pdfId: id, format };
      if (quizScope === 'current') {
        body.firstPage = currentPage;
        body.lastPage = currentPage;
      } else if (quizScope === 'range') {
        body.firstPage = Number(quizFrom) || 1;
        body.lastPage = Number(quizTo) || Number(quizFrom) || 1;
      }
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/ai/quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Quiz generation failed.');
      setAiModal({ open: true, loading: false, error: null, title: label, text: '', questions: data.questions, materialTitle: data.materialTitle || '' });
    } catch (err) {
      setAiModal({ open: true, loading: false, error: err.message, title: label, text: '', questions: null, materialTitle: '' });
    }
  };

  const isOwner = user && material && user.id === material.owner_id;
  const hasLatexCode = Boolean(material?.latex_code);

  // Prefer returning to wherever the reader was opened from; only fall back
  // to the Library when there's no in-app history to go back to (e.g. the
  // reader was opened directly via a shared link).
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/${lang}/library`);
    }
  };
  const isOriginallyLatex = Boolean(material?.url?.includes('-latex.pdf') && (!material?.latex_url || material?.latex_url === material?.url));

  const handleCompileSuccess = (url) => {
    if (isOriginallyLatex) {
      setMaterial(prev => ({ ...prev, url: url }));
    } else {
      setMaterial(prev => ({ ...prev, latex_url: url }));
      setViewMode('latex');
    }
  };

  const handleSaveSuccess = (newLatex) => {
    setMaterial(prev => ({ ...prev, latex_code: newLatex }));
    setGeneratedLatex(newLatex);
  };

  const [activeSourceTab, setActiveSourceTab] = useState('All');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [readerDark, setReaderDark] = useState(false);

  const friendsProgress = [
    { id: 1, name: 'Ahmed K.', status: '2h ago', chapter: 'Chapter 3 — Rules of Differentiation', points: 23, initial: 'AK' },
    { id: 2, name: 'Leyla M.', status: '1d ago', chapter: 'Chapter 5 — Integration Basics', points: 45, initial: 'LM' },
    { id: 3, name: 'Rashad H.', status: '3d ago', chapter: 'Chapter 2 — Continuity & Derivatives', points: 18, initial: 'RH' },
  ];

  const sourceTabs = [
    { id: 'All', icon: <Layers size={14} />, label: 'All' },
    { id: 'YouTube', icon: <Youtube size={14} />, label: 'YouTube' },
    { id: 'Web', icon: <Globe size={14} />, label: 'Web' },
    { id: 'PDF', icon: <FileText size={14} />, label: 'PDF' },
    { id: 'Google Docs', icon: <FileSpreadsheet size={14} />, label: 'Google Docs' },
  ];

  const sourcesList = material?.sources || [];
  const filteredSources = activeSourceTab === 'All' 
    ? sourcesList 
    : sourcesList.filter(s => s.type === activeSourceTab);

  const dynamicSections = useMemo(() => {
    if (!material?.latex_code) return [];
    
    // Regex to find \section, \subsection, \subsubsection (with or without *)
    const sectionRegex = /\\(section|subsection|subsubsection)\*?\{(.*?)\}/g;
    const foundSections = [];
    let match;
    let idCounter = 1;
    
    while ((match = sectionRegex.exec(material.latex_code)) !== null) {
      const type = match[1];
      const title = match[2];
      
      foundSections.push({
        id: idCounter++,
        title: title,
        level: type === 'section' ? 0 : type === 'subsection' ? 1 : 2,
        page: 1, // Defaulting to 1 for now
        duration: ''
      });
    }
    return foundSections;
  }, [material?.latex_code]);

  // Set total pages based on document availability
  useEffect(() => {
    if (!material?.url && hasLatexCode) {
      setTotalPages(dynamicSections.length || 1);
    }
  }, [material?.url, hasLatexCode, dynamicSections.length]);

  // Scroll to section in mock view
  useEffect(() => {
    if (!material?.url && activeSection && scrollContainerRef.current) {
      const element = scrollContainerRef.current.querySelector(`[data-section-id="${activeSection}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }
  }, [activeSection, material?.url]);

  // Save reading progress to localStorage and DB whenever page changes (per-view)
  // Only runs AFTER progress has been restored from localStorage to avoid overwriting it
  useEffect(() => {
    if (!id || !isProgressRestored.current || !token) return;
    
    const cur = viewProgress[viewMode];
    const progressKey = `reading_progress_${id}_${viewMode}`;
    const newMax = Math.max(cur.maxPageReached, cur.currentPage);
    localStorage.setItem(progressKey, JSON.stringify({ page: cur.currentPage, maxPage: newMax }));
    
    // Sync to backend DB
    const syncProgress = async () => {
      try {
        await fetch('/api/materials/progress', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            pdfId: id,
            currentPage: cur.currentPage,
            totalPages: cur.totalPages || 1
          })
        });
      } catch (err) {
        console.error('Error syncing reading progress:', err);
      }
    };
    
    syncProgress();

    if (newMax !== cur.maxPageReached) {
      setViewProgress(prev => ({
        ...prev,
        [viewMode]: { ...prev[viewMode], maxPageReached: newMax }
      }));
    }
  }, [viewProgress[viewMode].currentPage, id, viewMode, token]);

  useEffect(() => {
    const fetchMaterial = async () => {
      console.log('Fetching material with ID:', id);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/materials/${id}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Material data received:', data);
        const matData = data.material || data;
        setMaterial(matData);
        
        // Use the actual stored page count if available, otherwise default to 1 
        // until the PDF renderer gives us the real count.
        setTotalPages(matData.pages || 1);

        // Restore saved reading progress from localStorage for both views
        const restoreProgress = (mode) => {
          const key = `reading_progress_${id}_${mode}`;
          const saved = localStorage.getItem(key);
          if (saved) {
            try {
              const { page, maxPage } = JSON.parse(saved);
              setViewProgress(prev => ({
                ...prev,
                [mode]: { currentPage: page || 1, totalPages: prev[mode].totalPages, maxPageReached: maxPage || page || 1 }
              }));
            } catch (_) {}
          }
        };
        restoreProgress('original');
        restoreProgress('latex');
        // Mark progress as restored so the save effect can now safely run
        isProgressRestored.current = true;

        // Also fetch user's saved status for this material
        const userRes = await fetch(`${process.env.REACT_APP_API_BASE_URL|| ""}/api/materials/all?page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          setIsSaved((userData.savedIds || []).includes(id));
        }

      } catch (error) {
        console.error('Error fetching material:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      fetchMaterial();
    } else {
      console.warn('Missing ID or Token:', { id, tokenHasValue: !!token });
    }
  }, [id, token]);

  const [myRating, setMyRating] = useState(null);
  const [myRatingRole, setMyRatingRole] = useState(null);
  const [ratingHover, setRatingHover] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    if (!id || !token) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/ratings/${id}/mine`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyRating(data.myRating);
          setMyRatingRole(data.myRole);
        }
      } catch (err) {
        console.error('Failed to fetch my rating:', err);
      }
    })();
  }, [id, token]);

  const submitRating = async (score) => {
    setSubmittingRating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/ratings/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ score })
      });
      const data = await res.json();
      if (res.ok) {
        setMyRating(data.myRating);
        setMyRatingRole(data.myRole);
        setMaterial(prev => prev ? { ...prev, rating: data.overall, reviewsCount: data.reviewsCount } : prev);
      }
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setSubmittingRating(false);
    }
  };

  // Track learning time every 60 seconds
  useEffect(() => {
    let intervalId;
    if (token && id) {
      intervalId = setInterval(async () => {
        try {
          await fetch('/api/analytics/track-time', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (err) {
          console.error("Error tracking time:", err);
        }
      }, 60000);
    }
    return () => clearInterval(intervalId);
  }, [id, token]);

  const handleToggleSave = async () => {
    try {
      const response = await fetch('/api/materials/toggle-save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pdfId: id })
      });
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data.isSaved);
      }
    } catch (error) {
      console.error("Error saving material:", error);
    }
  };

  const toggleSidebar = (key) => {
    setSidebarRightExpanded(prev => {
      const isCurrentlyOpen = prev[key];
      // Close all and only open the requested one if it wasn't already open
      return {
        aiHelp: false,
        askQuestion: false,
        friends: false,
        sources: false,
        quiz: false,
        rating: false,
        [key]: !isCurrentlyOpen
      };
    });
  };

  const calculateProgress = () => {
    if (totalPages <= 1) return 0;
    return Math.round((maxPageReached / totalPages) * 100);
  };

  const isFullyComplete = maxPageReached >= totalPages && totalPages > 1;

  const handleMarkAllComplete = () => {
    if (!id || !isProgressRestored.current) return;
    const progressKey = `reading_progress_${id}_${viewMode}`;
    localStorage.setItem(progressKey, JSON.stringify({ page: totalPages, maxPage: totalPages }));
    setViewProgress(prev => ({
      ...prev,
      [viewMode]: { currentPage: totalPages, totalPages, maxPageReached: totalPages }
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="text-foreground text-sm opacity-40 animate-pulse">Opening your resource...</span>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
        <div className="p-8 rounded-2xl bg-card border border-border shadow-xl max-w-md w-full">
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-6">
                {error || "We couldn't find the resource you're looking for."}
            </p>
            <button 
                onClick={() => navigate(`/${lang}/library`)}
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-medium"
            >
                Back to Library
            </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`pdf-viewer-container ${readerDark ? 'reader-dark' : ''}`}>
      {/* Left Sidebar */}
      <aside className="viewer-sidebar-left">
        <div className="flex items-start gap-3 mb-10">
          <button
            onClick={handleBack}
            className="mt-0.5 text-[#0d282d] hover:text-[#739d94] transition-colors"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>
          <div className="flex flex-col min-w-0">
            <h1 className="text-[13px] font-bold text-[#0d282d] truncate">{material?.title || "Untitled Document"}</h1>
            <p className="text-[10px] text-[#64748b] font-medium truncate">by {material?.uploader || material?.author || "Anonymous"}{material?.university ? ` • ${material.university}` : ""}</p>
            {material?.createdAt && (
              <p className="text-[9px] text-[#94a3b8] font-medium truncate mt-0.5 flex items-center gap-1">
                <span>Uploaded {formatDate(material.createdAt, user?.date_format)}</span>
                <span>•</span>
                <span className="flex items-center gap-0.5"><EyeIcon size={10} /> {formatCount(material.views)}</span>
                {isOwner && material.uniqueViewers !== undefined && (
                  <span>• {material.uniqueViewers} unique reader{material.uniqueViewers === 1 ? '' : 's'}</span>
                )}
              </p>
            )}
          </div>
        </div>

        {(material?.url || (hasLatexCode && dynamicSections.length > 0)) && (
          <div className="progress-section">
            <div className="progress-header mb-2 flex justify-between items-end">
              <span className="text-[10px] font-bold text-[#64748b] uppercase letter-spacing-[0.05em]">Reading Progress</span>
              <span className="text-xs font-bold text-[#0d282d] font-mono">{calculateProgress()}%</span>
            </div>
            <div className="h-1.5 bg-[#739d94]/20 rounded-full overflow-hidden mb-2 relative">
              {/* Furthest page reached (saved progress) */}
              <div
                className="h-full bg-[#739d94] transition-all duration-700 ease-out rounded-full"
                style={{ width: `${calculateProgress()}%` }}
              />
              {/* Current page position indicator */}
              {totalPages > 1 && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#739d94] border-2 border-white shadow-sm transition-all duration-300"
                  style={{ left: `calc(${Math.round((currentPage / totalPages) * 100)}% - 5px)` }}
                />
              )}
            </div>
            <div className="flex justify-between items-center">
              <p className="text-[9px] font-bold text-[#64748b] uppercase tracking-widest">
                PAGE {currentPage} OF {totalPages}
              </p>
              {maxPageReached > currentPage && (
                <p className="text-[9px] font-bold text-[#739d94] uppercase tracking-widest">
                  READ UP TO P.{maxPageReached}
                </p>
              )}
            </div>
          </div>
        )}

        {hasLatexCode && dynamicSections.length > 0 && (
          <nav className="toc-section mt-8">
            <h3 className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-[0.1em] mb-4">Table of Contents</h3>
            <div className="flex flex-col gap-1">
              {dynamicSections.map((section, idx) => (
                <div
                  key={section.id}
                  className={`group flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${activeSection === section.id ? 'bg-[#f1f5f9]' : 'hover:bg-[#739d94]/5'}`}
                  style={{ marginLeft: `${section.level * 16}px` }}
                  onClick={() => {
                    setActiveSection(section.id);
                    setCurrentPage(section.page);
                  }}
                >
                  <div className={`mt-1.5 ${section.level === 0 ? 'w-2 h-2' : section.level === 1 ? 'w-1.5 h-1.5' : 'w-1 h-1'} rounded-full border-2 flex-shrink-0 ${activeSection === section.id ? 'border-[#739d94] bg-[#739d94]' : 'border-[#e2e8f0] group-hover:border-[#739d94] bg-transparent'}`} />
                  <div className="flex flex-col">
                    <span className={`text-xs ${activeSection === section.id ? 'font-bold text-[#0d282d]' : 'font-medium text-[#64748b] group-hover:text-[#0d282d]'}`}>{section.title}</span>
                    {section.level === 0 && <span className="text-[10px] font-medium text-[#64748b] mt-0.5">Section {idx + 1}</span>}
                  </div>
                </div>
              ))}
            </div>
          </nav>
        )}

        {(material?.url || hasLatexCode) && (
          <div className="mt-auto pt-8 flex flex-col gap-4">
              <div className="flex items-center justify-between p-3.5 rounded-xl border border-[#e2e8f0] bg-[#f8fafc]">
                  <div className="flex items-center gap-2">
                      <Eye size={14} className="text-[#64748b]" />
                      <span className="text-[10px] font-bold text-[#64748b]">{totalPages} pages • {Math.round(totalPages * 3 / 60)}h read</span>
                  </div>
              </div>
              <button
                onClick={handleMarkAllComplete}
                disabled={isFullyComplete}
                className={`w-full py-3.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  isFullyComplete
                    ? 'bg-[#739d94]/10 text-[#739d94] cursor-default border border-[#739d94]/20'
                    : 'text-[#0d282d] hover:bg-[#739d94]/5 border border-transparent hover:border-[#e2e8f0]'
                }`}
              >
                {isFullyComplete ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Completed!
                  </>
                ) : (
                  'Mark All Complete'
                )}
              </button>
          </div>
        )}
      </aside>

      {/* Main Reader */}
      <main className="viewer-main-content">
        {/* View Switcher Tabs (Inspired by User Sketch) */}
        <div className="flex items-center justify-center border-b border-[#e2e8f0] bg-white/50 backdrop-blur-md sticky top-0 z-10 py-2 relative">
            <div className="flex p-1 bg-[#f1f5f9] rounded-2xl shadow-inner border border-[#e2e8f0]">
                <button
                    onClick={() => setViewMode('original')}
                    className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-bold transition-all ${viewMode === 'original' ? 'bg-white text-[#0d282d] shadow-sm' : 'text-[#64748b] hover:text-[#0d282d]'}`}
                >
                    <FileText size={14} />
                    Original File
                </button>
                {material?.latex_url && (
                    <button
                        onClick={() => setViewMode('latex')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-xl text-xs font-bold transition-all ${viewMode === 'latex' ? 'bg-white text-[#0d282d] shadow-sm' : 'text-[#64748b] hover:text-[#0d282d]'}`}
                    >
                        <FileCode size={14} />
                        LaTeX Notes
                    </button>
                )}
            </div>
            {material?.url && (
                <button
                    onClick={() => setReaderDark((prev) => !prev)}
                    title={readerDark ? 'Switch to light reading mode' : 'Switch to dark reading mode'}
                    className="absolute right-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-[#64748b] hover:text-[#0d282d] hover:bg-[#f1f5f9] transition-colors bg-transparent border border-transparent hover:border-[#e2e8f0]"
                >
                    {readerDark ? <Sun size={14} /> : <Moon size={14} />}
                </button>
            )}
        </div>

        {(material?.url || hasLatexCode) && (
          <section className="pdf-canvas-container flex flex-col items-center">
            {material?.url ? (
              <PDFRenderer 
                url={
                  viewMode === 'latex' && material.latex_url
                    ? (material.latex_url.startsWith('http') ? material.latex_url : `${process.env.REACT_APP_API_BASE_URL|| ""} ${material.latex_url.startsWith('/') ? '' : '/'}${material.latex_url}`)
                    : (material.url.startsWith('http') ? material.url : `${process.env.REACT_APP_API_BASE_URL|| ""} ${material.url.startsWith('/') ? '' : '/'}${material.url}`)
                } 
                currentPage={currentPage}
                zoom={zoom}
                dark={readerDark}
                onDocumentLoadSuccess={({ numPages }) => {
                  // Only update total pages if this is a new value for the current view
                  setViewProgress(prev => {
                    if (prev[viewMode].totalPages === numPages) return prev;
                    // Also cap currentPage/maxPageReached to numPages to prevent > 100%
                    return {
                      ...prev,
                      [viewMode]: {
                        ...prev[viewMode],
                        totalPages: numPages,
                        currentPage: Math.min(prev[viewMode].currentPage, numPages),
                        maxPageReached: Math.min(prev[viewMode].maxPageReached, numPages),
                      }
                    };
                  });
                }}
              />
            ) : (
              <div className="page-content" ref={scrollContainerRef}>
                <div className="page-num">Page {currentPage}</div>
                <h1 className="text-2xl font-bold mb-6 text-[#0d282d]">{material?.title || "Document Structure"}</h1>

                <div className="flex flex-col gap-8 w-full max-w-2xl">
                  {dynamicSections.map((section, idx) => (
                    <div
                      key={section.id}
                      data-section-id={section.id}
                      className={`section-block transition-all duration-500 ${activeSection === section.id ? 'active-highlight ring-2 ring-[#739d94]/20 p-6 rounded-2xl -m-6 bg-[#739d94]/5' : 'opacity-60'}`}
                    >
                      {section.level === 0 && (
                        <h2 className="text-xl font-bold mb-3 text-[#0d282d] flex items-center gap-2">
                          <span className="text-[#739d94]">§{idx + 1}</span> {section.title}
                        </h2>
                      )}
                      {section.level === 1 && (
                        <h3 className="text-lg font-semibold mb-2 text-[#0d282d]/90 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#739d94]/60" /> {section.title}
                        </h3>
                      )}
                      {section.level === 2 && (
                        <h4 className="text-base font-medium mb-2 text-[#0d282d]/80 italic flex items-center gap-2">
                          <ChevronRight size={14} className="text-[#739d94]/40" /> {section.title}
                        </h4>
                      )}

                      <div className="space-y-3 mt-2">
                        <div className="h-2 w-full bg-[#f1f5f9] rounded-full animate-pulse" />
                        <div className="h-2 w-[90%] bg-[#f1f5f9] rounded-full animate-pulse" />
                        <div className="h-2 w-[95%] bg-[#f1f5f9] rounded-full animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>

                {dynamicSections.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 opacity-40">
                    <FileCode size={48} className="mb-4" />
                    <p>No sections found in LaTeX code.</p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        <div className="viewer-controls-floating">
          <button onClick={() => setZoom(z => Math.max(50, z - 10))} className="control-btn" title="Zoom Out">
            <Search size={18} />
          </button>
          <span className="zoom-indicator">{zoom}%</span>
          <button onClick={() => setZoom(z => Math.min(200, z + 10))} className="control-btn" title="Zoom In">
            <ZoomIn size={18} />
          </button>
          
          <div className="divider" />

          <button
            onClick={handleToggleSave}
            className={`control-btn ${isSaved ? 'active-emerald' : ''}`}
            title={isSaved ? "Remove from My Notes" : "Save to My Notes"}
          >
            <Bookmark size={18} className={isSaved ? 'fill-current' : ''} />
          </button>

          <div className="divider" />
          
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="control-btn" title="Previous Page">
            <ChevronLeft size={20} />
          </button>
          <div className="page-indicator">
            Page {currentPage} of {totalPages}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="control-btn" title="Next Page">
            <ChevronRight size={20} />
          </button>
        </div>
      </main>

      {/* Sidebar Toggle Button */}
      <button
        className={`sidebar-toggle-btn ${sidebarOpen ? 'open' : 'closed'}`}
        onClick={() => setSidebarOpen(prev => !prev)}
        title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Right Sidebar */}
      <aside className={`viewer-sidebar-right ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="sidebar-scroll-content">
          {/* Rate this Material Section */}
          <div className="sidebar-collapsible">
            <div className="collapsible-header" onClick={() => toggleSidebar('rating')}>
              <div className="title-group">
                <Star size={16} />
                <span className="title">Rate this Material</span>
              </div>
              <ChevronDown size={14} className={`chevron-icon ${sidebarRightExpanded.rating ? 'rotate-180' : ''}`} />
            </div>
            <AnimatePresence>
              {sidebarRightExpanded.rating && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="collapsible-content"
                >
                  <div className="rating-container">
                    <div className="rating-summary">
                      <span className="rating-score">{(material?.rating || 0).toFixed(1)}</span>
                      <span className="rating-outof">/ 10</span>
                      <span className="rating-count">{material?.reviewsCount || 0} rating{material?.reviewsCount === 1 ? '' : 's'}</span>
                    </div>
                    <p className="description">
                      {myRating ? `You rated this ${myRating}/10 as a ${myRatingRole}.` : 'Rate this material from 1 to 10.'}
                    </p>
                    <div className="rating-stars" onMouseLeave={() => setRatingHover(0)}>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          type="button"
                          className="rating-star-btn"
                          disabled={submittingRating}
                          onMouseEnter={() => setRatingHover(n)}
                          onClick={() => submitRating(n)}
                          title={`${n}/10`}
                        >
                          <Star
                            size={18}
                            className={(ratingHover || myRating || 0) >= n ? 'star-filled' : 'star-empty'}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* AI Help Section */}
          <div className={`sidebar-collapsible ${sidebarRightExpanded.aiHelp ? 'expanded' : ''}`}>
            <div className="collapsible-header" onClick={() => toggleSidebar('aiHelp')}>
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="icon-ai" />
                <span className="title">AI Help</span>
              </div>
              <ChevronRight size={16} className={`chevron-icon ${sidebarRightExpanded.aiHelp ? 'rotate-90' : ''}`} />
            </div>
            
            <AnimatePresence>
              {sidebarRightExpanded.aiHelp && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="collapsible-content"
                >
                  <div className="ai-help-options">
                    <p className="description">What type of help do you need?</p>
                    
                    <button className="ai-option-btn" onClick={() => runAiTextAction('explain', 'Explain this page')}>
                      <div className="icon-wrap"><Zap size={16} /></div>
                      <div className="btn-text">
                        <span className="label">Explain this page</span>
                        <span className="sub">AI breaks down the current content</span>
                      </div>
                    </button>

                    <button className="ai-option-btn" onClick={() => runAiTextAction('summarize', 'Summarize key concepts')}>
                      <div className="icon-wrap"><BookOpen size={16} /></div>
                      <div className="btn-text">
                        <span className="label">Summarize key concepts</span>
                        <span className="sub">Get a quick overview</span>
                      </div>
                    </button>

                    <button className="ai-option-btn" onClick={() => runAiTextAction('simplify', 'Simplify the formula')}>
                      <div className="icon-wrap"><Activity size={16} /></div>
                      <div className="btn-text">
                        <span className="label">Simplify the formula</span>
                        <span className="sub">Step-by-step walkthrough</span>
                      </div>
                    </button>

                    <button className="ai-option-btn">
                      <div className="icon-wrap"><HelpCircle size={16} /></div>
                      <div className="btn-text">
                        <span className="label">Ask a teacher or helper</span>
                        <span className="sub">Route to @teachers or @helpers group</span>
                      </div>
                    </button>

                    {(!hasLatexCode || isOwner) && (
                      <button className="ai-option-btn" onClick={() => {
                        if (hasLatexCode) {
                          setGeneratedLatex(material.latex_code);
                        } else {
                          setGeneratedLatex('');
                        }
                        setIsLatexModalOpen(true);
                      }}>
                        <div className="icon-wrap"><FileCode size={16} /></div>
                        <div className="btn-text">
                          <span className="label">{hasLatexCode ? "Edit LaTeX Source" : "Convert to LaTeX"}</span>
                          <span className="sub">{hasLatexCode ? "Modify and re-compile existing code" : "Generate and edit LaTeX code"}</span>
                        </div>
                      </button>
                    )}

                    <div className="ai-status">
                      <MessageCircle size={12} />
                      <span>Viewing page {currentPage}. Ask me anything.</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Ask a Question Section */}
          <div className="sidebar-collapsible">
            <div className="collapsible-header" onClick={() => toggleSidebar('askQuestion')}>
              <div className="title-group">
                <MessageSquare size={16} />
                <span className="title">Ask a Question</span>
              </div>
              <ChevronDown size={14} className={`chevron-icon ${sidebarRightExpanded.askQuestion ? 'rotate-180' : ''}`} />
            </div>
            <AnimatePresence>
              {sidebarRightExpanded.askQuestion && (
                <motion.div 
                   initial={{ height: 0, opacity: 0 }}
                   animate={{ height: 'auto', opacity: 1 }}
                   exit={{ height: 0, opacity: 0 }}
                   className="collapsible-content"
                >
                  <div className="ask-question-container">
                    <p className="description">Tag @teachers, @helpers, or specific users. They'll get a notification.</p>
                    <div className="textarea-wrapper">
                      <textarea placeholder='e.g. "When we have f=ma, why not g=mg? @helpers"' />
                      <div className="textarea-controls">
                        <button className="control-icon"><Camera size={18} /></button>
                        <button className="send-btn"><Send size={16} /></button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Friends' Progress Section */}
          <div className="sidebar-collapsible">
            <div className="collapsible-header" onClick={() => toggleSidebar('friends')}>
              <div className="title-group">
                <Users size={16} />
                <span className="title">Friends' Progress</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge">3</span>
                <ChevronDown size={14} className={`chevron-icon ${sidebarRightExpanded.friends ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <AnimatePresence>
              {sidebarRightExpanded.friends && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="collapsible-content"
                >
                  <div className="friends-list">
                    <p className="description">See how your friends are doing with this material.</p>
                    {friendsProgress.map(friend => (
                      <div key={friend.id} className="friend-card">
                        <div className="friend-avatar">{friend.initial}</div>
                        <div className="friend-info">
                          <div className="name-row">
                            <span className="name">{friend.name}</span>
                            <span className="time">{friend.status}</span>
                          </div>
                          <div className="progress-row">
                            <span className="chapter"><Star size={12} className="fill-pink-500 text-pink-500" /> {friend.chapter}</span>
                          </div>
                        </div>
                        <div className="points-badge">
                          <Star size={12} className="fill-[#d4af37] text-[#d4af37]" />
                          <span>{friend.points}pt</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sources Section */}
          <div className="sidebar-collapsible">
            <div className="collapsible-header" onClick={() => toggleSidebar('sources')}>
              <div className="title-group">
                <Share2 size={16} />
                <span className="title">Sources</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge">{sourcesList.length}</span>
                <ChevronDown size={14} className={`chevron-icon ${sidebarRightExpanded.sources ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <AnimatePresence>
              {sidebarRightExpanded.sources && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="collapsible-content"
                >
                  <div className="sources-container">
                    <div className="tabs-scroll">
                      <div className="source-tabs">
                        {sourceTabs.map(tab => (
                          <button 
                            key={tab.id}
                            className={`source-tab ${activeSourceTab === tab.id ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveSourceTab(tab.id);
                            }}
                          >
                            {tab.icon}
                            <span>{tab.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="sources-list">
                      {filteredSources.map(source => (
                        <a href={source.url} target="_blank" rel="noopener noreferrer" key={source.id} className="source-item">
                          <div className={`source-icon ${source.type.toLowerCase()}`}>
                            {source.type === 'YouTube' ? <Youtube size={14} /> : 
                             source.type === 'Web' ? <Globe size={14} /> : 
                             <FileText size={14} />}
                          </div>
                          <div className="source-info">
                            <span className="source-title">{source.title}</span>
                            <span className="source-url">{source.url}</span>
                            <span className={`source-tag ${source.type.toLowerCase()}`}>
                              {source.isVideo ? 'YOUTUBE VIDEO' : `${source.type.toUpperCase()} DOCUMENT`}
                            </span>
                          </div>
                          <Share2 size={14} className="share-icon" />
                        </a>
                      ))}
                      {filteredSources.length === 0 && (
                        <div className="empty-sources">
                          No sources found for this category.
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Create Quiz Section */}
          <div className="sidebar-collapsible">
            <div className="collapsible-header" onClick={() => toggleSidebar('quiz')}>
              <div className="title-group">
                <FileQuestion size={16} />
                <span className="title">Create Quiz</span>
              </div>
              <ChevronDown size={14} className={`chevron-icon ${sidebarRightExpanded.quiz ? 'rotate-180' : ''}`} />
            </div>
            <AnimatePresence>
              {sidebarRightExpanded.quiz && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="collapsible-content"
                >
                  <div className="quiz-container">
                    <p className="description">Generate quiz questions from this material to test your understanding.</p>

                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                      {[
                        { id: 'all', label: 'All pages' },
                        { id: 'current', label: `Page ${currentPage}` },
                        { id: 'range', label: 'Range' },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setQuizScope(opt.id)}
                          style={{
                            flex: 1,
                            padding: '6px 4px',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: quizScope === opt.id ? '#739d94' : 'rgba(148,163,184,0.35)',
                            background: quizScope === opt.id ? 'rgba(115,157,148,0.15)' : 'transparent',
                            color: quizScope === opt.id ? '#0d282d' : '#64748b',
                            fontSize: '10px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {quizScope === 'range' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                        <input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={quizFrom}
                          onChange={(e) => setQuizFrom(e.target.value)}
                          style={{ width: '60px', padding: '5px 8px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.35)', fontSize: '11px' }}
                        />
                        <span style={{ fontSize: '10px', color: '#64748b' }}>to</span>
                        <input
                          type="number"
                          min={quizFrom || 1}
                          max={totalPages}
                          value={quizTo}
                          onChange={(e) => setQuizTo(e.target.value)}
                          style={{ width: '60px', padding: '5px 8px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.35)', fontSize: '11px' }}
                        />
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>of {totalPages}</span>
                      </div>
                    )}

                    <div className="quiz-options">
                      <button className="quiz-option" onClick={() => runAiQuiz('multiple_choice', 'Multiple Choice Quiz')}>
                        <FileQuestion size={14} />
                        <span className="label">Multiple Choice (10 questions)</span>
                      </button>
                      <button className="quiz-option" onClick={() => runAiQuiz('true_false', 'True / False Quiz')}>
                        <FileQuestion size={14} />
                        <span className="label">True / False (15 questions)</span>
                      </button>
                      <button className="quiz-option" onClick={() => runAiQuiz('fill_blank', 'Fill in the Blank Quiz')}>
                        <FileQuestion size={14} />
                        <span className="label">Fill in the Blank (8 questions)</span>
                      </button>
                      <button className="quiz-option" onClick={() => runAiQuiz('mixed', 'Mixed Format Quiz')}>
                        <FileQuestion size={14} />
                        <span className="label">Mixed Format (12 questions)</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="cta-group">
            <button className="btn-primary flex items-center justify-center gap-2">
                Share Notes
            </button>
            <button className="btn-secondary flex items-center justify-center gap-2">
                <Settings size={14} /> System Settings
            </button>
        </div>
      </aside>

      <LaTeXEditorModal 
        isOpen={isLatexModalOpen} 
        onClose={() => setIsLatexModalOpen(false)} 
        initialLatex={generatedLatex}
        materialId={id}
        token={token}
        onCompileSuccess={handleCompileSuccess}
        onSaveSuccess={handleSaveSuccess}
        isOriginallyLatex={isOriginallyLatex}
      />

      <AIResultModal
        isOpen={aiModal.open}
        onClose={() => setAiModal(prev => ({ ...prev, open: false }))}
        loading={aiModal.loading}
        error={aiModal.error}
        title={aiModal.title}
        textContent={aiModal.text}
        questions={aiModal.questions}
        materialTitle={aiModal.materialTitle}
      />
    </div>
  );
};

export default PDFViewer;
